import { autoinject, ComponentAttached, computedFrom } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { RoutableComponentCanDeactivate, Router } from "aurelia-router";
import {
  ControllerValidateResult,
  ValidationController,
  ValidationControllerFactory,
  ValidationRules,
} from "aurelia-validation";
import { EventAggregator } from "aurelia-event-aggregator";
import { BootstrapFormRenderer } from "../../validation/bootstrap-form-renderer";
import {
  Anwendung,
  Berechnungsart,
  Foerderfluessigkeit,
  ProductfinderData,
  Pumpentyp,
  TypFoerderfluessigkeit,
  TypViskositaet,
} from "./productfinder-data";
import { ProductfinderService } from "./productfinder-service";
import { ApplicationState } from "../../services/application-state";
import { LocaleService } from "../../services/locale-service";
import { DialogFactory } from "../../services/dialog-factory";

@autoinject
export class ProductfinderForm
  implements ComponentAttached, RoutableComponentCanDeactivate
{
  data: ProductfinderData;
  computationRunning: boolean;

  form: HTMLFormElement;

  private validationController: ValidationController;

  constructor(
    private i18n: I18N,
    private validationControllerFactory: ValidationControllerFactory,
    private router: Router,
    private service: ProductfinderService,
    private localeService: LocaleService,
    private state: ApplicationState,
    private dialogFactory: DialogFactory,
    private eventAggregator: EventAggregator,
  ) {
    this.data = this.loadDataFromState(state);
    this.validationController =
      this.validationControllerFactory.createForCurrentScope();
    this.validationController.addRenderer(new BootstrapFormRenderer());

    type D = ProductfinderData;
    let tr = function (s: string) {
      return i18n.tr("productfinder.input." + s);
    };

    ValidationRules.ensure((d: D) => d.pumpentyp)
      .displayName(tr("pumpengroesse"))
      .required()
      .when((d: D) => d.berechnungsart == Berechnungsart.pumpe_nachrechnen)

      .ensure((d: D) => d.geraeuschoptimiert)
      .displayName(tr("geraeuschoptimiert"))
      .required()
      .when((d: D) => d.anwendung == Anwendung.schmieroelAnwendung)

      .ensure((d: D) => d.gewuenschterFoerderstrom)
      .displayName(tr("q_foerderstrom"))
      .required()
      .when((d: D) => d.berechnungsart == Berechnungsart.pumpe_auslegen)
      .then()
      .satisfiesRule("float")
      .satisfiesRule("positive")

      .ensure((d: D) => d.betriebsdruck)
      .displayName(tr("betriebsdruck"))
      .required()
      .then()
      .satisfiesRule("float")
      .satisfiesRule("positive")

      .ensure((d: D) => d.betriebsdrehzahl)
      .displayName(tr("betriebsdrehzahl"))
      .required()
      .when(
        (d: D) =>
          d.berechnungsart == Berechnungsart.pumpe_nachrechnen ||
          (d.anwendung == Anwendung.schmieroelAnwendung && !d.netzfrequenz),
      )
      .then()
      .satisfiesRule("float")
      .satisfiesRule("positive")

      .ensure((d: D) => d.marinekraftstoff)
      .displayName(tr("marinekraftstoff"))
      .required()
      .when(
        (d: D) =>
          !d.manuelleViskositaet &&
          d.anwendung == Anwendung.marinekraftstoffAnwendung,
      )

      .ensure((d: D) => d.schmieroel)
      .displayName(tr("schmieroel"))
      .required()
      .when(
        (d: D) =>
          !d.manuelleViskositaet &&
          d.anwendung == Anwendung.schmieroelAnwendung,
      )

      .ensure((d: D) => d.betriebstemperatur)
      .displayName(tr("betriebstemperatur"))
      .required()
      .when((d: D) => !d.manuelleViskositaet)
      .then()
      .satisfiesRule("float")

      .ensure((d: D) => d.anfahrtemperatur)
      .displayName(tr("anfahrtemperatur"))
      .required()
      .when(
        (d: D) =>
          !d.manuelleViskositaet &&
          d.anwendung == Anwendung.schmieroelAnwendung &&
          d.anfahrtemperaturEnabled,
      )
      .then()
      .satisfiesRule("float")

      .ensure((d: D) => d.betriebsviskositaet)
      .displayName(tr("betriebsviskositaet"))
      .required()
      .when((d: D) => d.manuelleViskositaet)
      .then()
      .satisfiesRule("float")
      .satisfiesRule("positive")

      .ensure((d: D) => d.startviskositaet)
      .displayName(tr("startviskositaet"))
      .required()
      .when((d: D) => d.manuelleViskositaet && d.startviskositaetEnabled)
      .then()
      .satisfiesRule("float")
      .satisfiesRule("positive")

      .ensure((d: D) => d.dichte)
      .displayName(tr("dichte"))
      .required()
      .when(
        (d: D) =>
          d.manuelleViskositaet && d.typViskositaet == TypViskositaet.dynamisch,
      )
      .then()
      .satisfiesRule("float")
      .satisfiesRule("positive")

      .on(this.data);

    // this dialog wants to be notified about locale changes
    this.eventAggregator.subscribe("i18n:locale:changed", () =>
      this.localeChanged(),
    );
  }

  private loadDataFromState(state: ApplicationState) {
    try {
      return this.state.productfinderData || new ProductfinderData();
    } catch (e) {
      console.error("could not load state from storage", e);
      return new ProductfinderData();
    }
  }

  /**
   * Clear the form when the locale changes. This is neccessary unfortunately
   * because the input elements for numerical values such as druck,
   * temperature or viskosity do not change their format masks and become
   * invalid. E.g. if a user entered a value of "3,4" in german locale, after
   * switching to english locale the value should be changed to "3.4"
   * automatically otherwise it gets invalid. Alas, the input components
   * cannot do this at the time being; implementing it would be a larger
   * effort. Therefore we take the simple approach of just clearing the form.
   */
  private localeChanged() {
    this.data.clear();
    this.state.productfinderData = this.data;
  }

  attached(): void {
    // Save the application state each time any element in the form is blurred!
    this.form.addEventListener(
      "blur",
      (ev) => (this.state.productfinderData = this.data),
      /*useCapture=*/ true,
    );
  }

  canDeactivate(): boolean {
    /*
         if (!this.isAllowedToLeave()) {
         return confirm(this.i18n.tr('reallyleave'));
         }
         */
    return true;
  }

  validate(property: string) {
    this.validationController.validate({
      object: this.data,
      propertyName: property,
    });
  }

  submit(): void {
    if (this.computationRunning) return;

    this.validationController
      .validate()
      .then((result: ControllerValidateResult) => {
        if (!result.valid) return;

        this.state.productfinderData = this.data;

        let clone = this.data.createCopyForServer(this.localeService);
        console.log("compute", clone);
        this.computationRunning = true;
        this.service
          .compute(clone)
          .then((r) => {
            console.log("compute result", r);
            this.computationRunning = false;
            // special case: Kap. 12.2 Schritt 3 Programmteil "Pumpe nachrechnen"
            // validation of betriebsdrehzahl
            if (
              r.error &&
              [
                "drehzahlNichtZulaessig",
                "drehzahlNichtZulaessigFuerViskositaet",
                "drehzahlNichtZulaessigKeineMesspunkteFuerViskositaet",
              ].indexOf(r.error.messageCode) >= 0
            ) {
              this.validationController.addError(
                this.i18n.tr(
                  "productfinder.output.messageCodes." + r.error.messageCode,
                  r.error.messageArgs,
                ),
                this.data,
                "betriebsdrehzahl",
              );
              return;
            }
            // store the result into persistent application state:
            this.state.productfinderResult = r;
            // enable the 'output' navigation rule:
            this.router.routes.filter(
              (r) => r.name == "output",
            )[0].settings.hidden = false;
            // enable the 'offer' navigation rule if no error occurred:
            this.router.routes.filter(
              (r) => r.name == "offer",
            )[0].settings.hidden = !!r.error;
            // go to the 'output' navigation rule:
            this.router.navigateToRoute("output");
          })
          .catch((e) => {
            this.computationRunning = false;
            this.dialogFactory.openError(
              this.i18n.tr("error.unexpected", { errorNo: e.errorNo }),
            );
          });
      })
      .catch((e) => console.log("unhandled rejection during validation", e));
  }

  setBerechnungsartPumpeNachrechnen(): void {
    this.data.berechnungsart = Berechnungsart.pumpe_nachrechnen;
    this.resetValidationFor("gewuenschterFoerderstrom", "betriebsdrehzahl");
  }

  setBerechnungsartPumpeAuslegen(): void {
    this.data.berechnungsart = Berechnungsart.pumpe_auslegen;
    this.resetValidationFor("pumpe", "betriebsdrehzahl");
  }

  anwendungChanged(): void {
    if (this.data.netzfrequenz !== 50 && this.data.netzfrequenz !== 60)
      this.data.netzfrequenz = 50;
    this.resetValidationFor(
      "anfahrtemperatur",
      "betriebsviskositaet",
      "startviskositaet",
      "dichte",
      "betriebsdrehzahl",
      "geraeuschoptimiert",
    );
    this.data.geraeuschoptimiert = false;
  }

  geraeuschoptimiertChanged(): void {
    this.resetValidationFor("pumpentyp", "geraeuschoptimiert");
    this.getPumpentypen("");
  }

  async getPumpentypen(search: string): Promise<Pumpentyp[]> {
    // Geräuschoptimierung is only relevant for Schmierölanwendungen, Marineölanwendungen should only see normal, not optimized, pumps
    let geraeuschoptimiert =
      this.data.geraeuschoptimiert &&
      this.data.anwendung === Anwendung.schmieroelAnwendung;
    const result = await this.service.getPumpentypen(
      search,
      geraeuschoptimiert,
    );
    let setType = false;
    for (let pump of result) {
      if (this.data.pumpentyp) {
        if (pump.text === this.data.pumpentyp.text) {
          pump.selected = true;
          setType = true;
        }
      }
    }
    // reset this selected pumpentyp on a geraeuschoptimiert change if the pump is only present for either one of the selection
    if (!setType) {
      (<any>this).data.pumpentyp = undefined;
    }
    return result;
  }

  async getMarinekraftstoffe(search: string): Promise<Foerderfluessigkeit[]> {
    const result = await this.service.getFoerderfluessigkeiten(
      TypFoerderfluessigkeit.marinekraftstoff,
      search,
    );
    return [
      new Foerderfluessigkeit(
        "-",
        this.i18n.tr("productfinder.input.select_marinekraftstoff_other"),
      ),
    ].concat(result);
  }

  async getSchmieroele(search: string): Promise<Foerderfluessigkeit[]> {
    const result = await this.service.getFoerderfluessigkeiten(
      TypFoerderfluessigkeit.schmieroel,
      search,
    );
    return [
      new Foerderfluessigkeit(
        "-",
        this.i18n.tr("productfinder.input.select_schmieroel_other"),
      ),
    ].concat(result);
  }

  reset() {
    this.data.clear();
    this.validationController.reset();
  }

  private resetValidationFor(...propertyNames: string[]): void {
    for (let propertyName of propertyNames) {
      this.validationController.reset({
        object: this.data,
        propertyName: propertyName,
      });
    }
  }

  @computedFrom("data.berechnungsart", "data.anwendung")
  get note(): string {
    let anwendung = "schmieroel";
    let berechnung = "auslegen";

    if (this.data.berechnungsart == Berechnungsart.pumpe_nachrechnen) {
      berechnung = "nachrechnen";
    }
    if (this.data.anwendung == Anwendung.marinekraftstoffAnwendung) {
      anwendung = "marine";
    }

    let key = "productfinder.notes.input." + anwendung + "." + berechnung;

    let note = this.i18n.tr(key);

    if (note === key) return "";

    return note;
  }
}
